import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gift, ShoppingBag, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";

export default function GiftCardPage() {
    const { user } = useAuth();
    const { fetchCart } = useCart();
    const navigate = useNavigate();
    const [config, setConfig] = useState({ denominations: [10, 25, 50, 100], validity_months: 24 });
    const [amount, setAmount] = useState(25);
    const [custom, setCustom] = useState(false);
    const [customAmount, setCustomAmount] = useState("");
    const [recipientName, setRecipientName] = useState("");
    const [recipientEmail, setRecipientEmail] = useState("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/gift-cards/config").then(({ data }) => setConfig(data)).catch(() => {});
    }, []);

    const finalAmount = custom ? Math.max(5, Math.min(500, parseFloat(customAmount) || 0)) : amount;

    const submit = async () => {
        if (!user) { navigate("/login?next=/gift-card"); return; }
        if (finalAmount < 5) { toast.error("Minimum bedrag is €5"); return; }
        setSubmitting(true);
        try {
            await api.post("/cart/items/gift-card", {
                amount: finalAmount,
                recipient_name: recipientName.trim(),
                recipient_email: recipientEmail.trim(),
                message: message.trim(),
            });
            await fetchCart();
            toast.success(`Cadeaubon van € ${finalAmount.toFixed(2)} toegevoegd aan mandje`);
            navigate("/cart");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="gift-card-page" className="bg-zinc-50 min-h-screen">
            <section className="border-b border-zinc-200 bg-white">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
                    <Link to="/stickers" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950">
                        <ArrowLeft size={14} /> Terug naar de winkel
                    </Link>
                    <div className="mt-4 flex items-end justify-between gap-3 flex-wrap">
                        <div>
                            <p className="label-tiny text-rose-600">★ Het perfecte cadeau</p>
                            <h1 className="font-display text-4xl md:text-5xl mt-2">Geef een cadeaubon.</h1>
                            <p className="text-zinc-600 mt-3 max-w-xl text-sm leading-relaxed">
                                Laat hen zelf hun favoriete sticker of accessoire kiezen.
                                Code wordt direct na betaling per e-mail verzonden. Geldig {config.validity_months} maanden.
                            </p>
                        </div>
                        <div className="border-2 border-rose-600 bg-rose-50 p-4 max-w-xs">
                            <Gift size={28} strokeWidth={1.5} className="text-rose-600" />
                            <p className="font-display text-xl mt-2 leading-tight">Persoonlijk &amp; flexibel</p>
                            <p className="text-xs text-zinc-600 mt-1">Voor verjaardagen, kerst, of zomaar.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
                <div className="space-y-6">
                    {/* Bedrag kiezen */}
                    <div className="border border-zinc-200 bg-white p-6">
                        <p className="label-tiny mb-3">★ Kies een bedrag</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                            {config.denominations.map(d => (
                                <button
                                    key={d}
                                    data-testid={`gc-amount-${d}`}
                                    onClick={() => { setAmount(d); setCustom(false); }}
                                    className={`py-4 border text-center transition-colors ${
                                        !custom && amount === d
                                            ? "border-zinc-950 bg-zinc-950 text-white"
                                            : "border-zinc-300 hover:border-zinc-950"
                                    }`}>
                                    <p className="font-display text-2xl">€ {d}</p>
                                </button>
                            ))}
                        </div>
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                                type="checkbox"
                                checked={custom}
                                onChange={(e) => setCustom(e.target.checked)}
                                className="w-4 h-4 accent-zinc-950"
                                data-testid="gc-custom-toggle"
                            />
                            Liever een ander bedrag? (€5-€500)
                        </label>
                        {custom && (
                            <input
                                type="number" min="5" max="500" step="5"
                                value={customAmount}
                                onChange={(e) => setCustomAmount(e.target.value)}
                                placeholder="bv. 35"
                                className="mt-2 w-32 px-3 py-2 border border-zinc-300 font-mono-dm text-sm focus:outline-none focus:border-zinc-950"
                                data-testid="gc-custom-amount"
                            />
                        )}
                    </div>

                    {/* Ontvanger info (optioneel) */}
                    <div className="border border-zinc-200 bg-white p-6 space-y-3">
                        <div>
                            <p className="label-tiny mb-1">★ Voor wie? <span className="text-zinc-400 normal-case">(optioneel — als je hem direct wil mailen)</span></p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="label-tiny mb-1 block">Naam ontvanger</label>
                                <input
                                    data-testid="gc-recipient-name"
                                    value={recipientName}
                                    onChange={(e) => setRecipientName(e.target.value)}
                                    placeholder="bv. Sophie"
                                    className="w-full px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950"
                                />
                            </div>
                            <div>
                                <label className="label-tiny mb-1 block">E-mail ontvanger</label>
                                <input
                                    data-testid="gc-recipient-email"
                                    type="email"
                                    value={recipientEmail}
                                    onChange={(e) => setRecipientEmail(e.target.value)}
                                    placeholder="sophie@…"
                                    className="w-full px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="label-tiny mb-1 block">Persoonlijke boodschap (max 280)</label>
                            <textarea
                                data-testid="gc-message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={3}
                                maxLength={280}
                                placeholder="Veel sticker-plezier! 🎉"
                                className="w-full px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950 resize-y"
                            />
                            <p className="text-[10px] text-zinc-400 mt-0.5">{message.length}/280</p>
                        </div>
                    </div>
                </div>

                {/* Sticky summary */}
                <aside className="space-y-4">
                    <div className="border-2 border-zinc-950 bg-zinc-950 text-white p-5">
                        <p className="label-tiny text-rose-400 mb-2">★ Jouw cadeaubon</p>
                        <p className="font-display text-5xl">€ {finalAmount.toFixed(2)}</p>
                        <p className="text-xs text-zinc-400 mt-2">Geldig {config.validity_months} maanden vanaf aankoop.</p>
                        <button
                            data-testid="gc-add-to-cart"
                            onClick={submit}
                            disabled={submitting || finalAmount < 5}
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3 bg-white text-zinc-950 hover:bg-zinc-100 text-sm font-bold uppercase tracking-widest disabled:opacity-60">
                            {submitting
                                ? <><Loader2 size={14} className="animate-spin" /> Bezig…</>
                                : <><ShoppingBag size={14} /> In mandje</>
                            }
                        </button>
                    </div>
                    <div className="border border-zinc-200 bg-white p-4 text-xs text-zinc-600 space-y-2">
                        <p className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" /> Code wordt na betaling automatisch per e-mail verzonden</p>
                        <p className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" /> Te gebruiken op alles in de shop</p>
                        <p className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" /> Restbedragen blijven beschikbaar</p>
                    </div>
                </aside>
            </section>
        </div>
    );
}
